<script setup lang="ts">
import LeSelect from "@dsk/components/Form/LeSelect.vue";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import LeLanguageSelectorDirectionSelector from "@/components/searchbar/LeLanguageSelectorDirectionSelector.vue";
import { MatomoHelper, SearchFilterHelper } from "@/helpers";
import {
  type LeSelectOption,
  MatomoEventAction,
  MatomoEventCategory,
  type SearchDirection,
  type SearchLanguage
} from "@/types";

interface Props {
  availableSourceLanguages: SearchLanguage[];
  availableTargetLanguages: SearchLanguage[];
  availableSearchDirections: SearchDirection[];
  sourceLanguage: SearchLanguage | undefined;
  direction: SearchDirection;
  targetLanguage: SearchLanguage | undefined;
}

const props = withDefaults(defineProps<Props>(), { direction: SearchFilterHelper.DEFAULT_DIRECTION });

const emit = defineEmits<{
  (event: "sourceLanguageChange", language?: SearchLanguage): void;
  (event: "directionChange", direction: SearchDirection): void;
  (event: "targetLanguageChange", language?: SearchLanguage): void;
}>();

const { t } = useI18n();

const allOption: LeSelectOption = {
  value: undefined,
  text: t("le-language-selector.all-languages")
};

const searchLanguageToSelectOption = (language: SearchLanguage) => ({ value: language.code, text: language.name });

const sourceLanguageOptions = computed<LeSelectOption[]>(() => [
  allOption,
  ...props.availableSourceLanguages.map(searchLanguageToSelectOption)
]);

const targetLanguageOptions = computed<LeSelectOption[]>(() => [
  allOption,
  ...props.availableTargetLanguages.map(searchLanguageToSelectOption)
]);

const isTargetLanguageDropdownDisabled = computed<boolean>(() => props.sourceLanguage === undefined);

const onChangeSourceLanguage = (languageCode?: string) => {
  MatomoHelper.sendEvent(
    MatomoEventCategory.Search,
    MatomoEventAction.PickLanguageFrom,
    "Clicked language-from option in dropdown"
  );

  emit(
    "sourceLanguageChange",
    languageCode ? props.availableSourceLanguages.find((language) => language.code === languageCode) : undefined
  );
};

const onChangeDirection = (direction: SearchDirection) => {
  emit("directionChange", direction);
};

const onChangeTargetLanguage = (languageCode?: string) => {
  MatomoHelper.sendEvent(
    MatomoEventCategory.Search,
    MatomoEventAction.PickLanguageTo,
    "Clicked language-to option in dropdown"
  );

  emit(
    "targetLanguageChange",
    languageCode ? props.availableTargetLanguages.find((language) => language.code === languageCode) : undefined
  );
};

const onReset = () => {
  MatomoHelper.sendEvent(
    MatomoEventCategory.Search,
    MatomoEventAction.ResetLanguageSelector,
    "Clicked reset language selector button"
  );

  emit("sourceLanguageChange", undefined);
  emit("targetLanguageChange", undefined);
  emit("directionChange", "two-way");
};
</script>

<template>
  <div class="le-language-selector">
    <div class="le-language-selector__element">
      <LeSelect
        class="le-language-selector__select le-language-selector__select--first"
        width="100%"
        :aria-label="t('le-language-selector.from-language')"
        :model-value="props.sourceLanguage?.code"
        :options="sourceLanguageOptions"
        @update:model-value="onChangeSourceLanguage($event)"
      />
    </div>

    <div class="le-language-selector__element le-language-selector__element--narrow">
      <LeLanguageSelectorDirectionSelector
        :options="availableSearchDirections"
        :model-value="props.direction"
        @update:model-value="onChangeDirection($event)"
      />
    </div>

    <div class="le-language-selector__element">
      <LeSelect
        class="le-language-selector__select le-language-selector__select--last"
        width="100%"
        :aria-label="t('le-language-selector.to-language')"
        :disabled="isTargetLanguageDropdownDisabled"
        :model-value="props.targetLanguage?.code"
        :options="targetLanguageOptions"
        @update:model-value="onChangeTargetLanguage($event)"
      />
    </div>

    <i18n-t
      v-if="props.sourceLanguage || props.targetLanguage"
      class="le-language-selector__reset"
      tag="div"
      keypath="le-language-selector.reset-text"
    >
      <template #button>
        <button class="le-language-selector__reset-button" role="button" @click="onReset()">
          {{ t("le-language-selector.reset-button") }}
        </button>
      </template>
    </i18n-t>
  </div>
</template>

<style lang="less" scoped>
@import "@dsk/styles/all";

.le-language-selector {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;

  ::v-deep(&__select--first .le-select__button) {
    border-right: none;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
  }

  ::v-deep(&__select--last .le-select__button) {
    border-left: none;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
  }

  &__element {
    flex: 1;

    &--narrow {
      flex: 0;
    }
  }

  &__reset {
    flex-basis: 100%;
    margin-top: 8px;
    .body-text("s");
  }

  &__reset-button {
    padding: 0;
    border: none;
    background-color: unset;
    .link();
  }
}
</style>
